import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { Play, Heart, ChevronRight, Clock, Calendar } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const Home = () => {
  const { language } = useLanguage();
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  const [announcements, setAnnouncements] = useState([]);
  const [livestream, setLivestream] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
    fetchLivestream();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements');
      const data = Array.isArray(response.data) ? response.data : [];
      setAnnouncements(data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]); // Set empty array on error
    }
  };

  const fetchLivestream = async () => {
    try {
      const response = await api.get('/livestream/active');
      setLivestream(response.data);
    } catch (error) {
      console.error('Error fetching livestream:', error);
    }
  };

  return (
    <>
      <PageMeta 
        path="/"
        description="Welcome to Mekane Hiwet Eyesus Dallas Orthodox Tewahdo Church. Join our community for worship, prayer, and spiritual growth. Services in Tigrinya, Geez, and English."
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className={`absolute inset-0 ${themeClasses.gradient}`}></div>
        
        {/* Decorative elements - these will adapt to theme */}
        <div className="absolute top-20 right-10 w-72 h-72 opacity-20 animate-blob" style={{background: 'var(--theme-color-300)', filter: 'blur(80px)'}}></div>
        <div className="absolute top-40 left-10 w-72 h-72 opacity-20 animate-blob animation-delay-2000" style={{background: 'var(--theme-color-400)', filter: 'blur(80px)'}}></div>
        <div className="absolute -bottom-20 left-1/2 w-72 h-72 opacity-20 animate-blob animation-delay-4000" style={{background: 'var(--theme-color-500)', filter: 'blur(80px)'}}></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_188dffff-cf47-49bb-8808-5c961ec9946c/artifacts/exaakh62_channels4_profile.jpg" 
              alt="Church Logo" 
              className="h-32 w-32 md:h-40 md:w-40 rounded-full shadow-2xl border-4 border-white dark:border-gray-700"
            />
          </div>
          
          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold mb-4 ${themeClasses.heroText}`} style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'home.welcome')}
            <span className={`block mt-2 ${themeClasses.primaryText}`}>
              {t(language, 'home.churchName')}
            </span>
          </h1>
          
          <p className={`text-lg md:text-xl ${themeClasses.heroText} opacity-90 mb-4 max-w-3xl mx-auto`} style={{fontFamily: 'Inter, sans-serif'}}>
            {t(language, 'home.subtitle')}
          </p>
          
          <p className={`text-base md:text-lg ${themeClasses.heroText} opacity-80 mb-10 max-w-2xl mx-auto`} style={{fontFamily: 'Inter, sans-serif'}}>
            {t(language, 'home.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {livestream && livestream.is_live ? (
              <Link
                to="/livestream"
                className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 group animate-pulse"
                data-testid="watch-live-btn"
              >
                <div className="w-3 h-3 bg-white rounded-full mr-2"></div>
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>{t(language, 'home.watchLive')}</span>
              </Link>
            ) : (
              <Link
                to="/sermons"
                className="px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 group"
                data-testid="latest-sermons-btn"
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>{t(language, 'home.watchLatestSermons')}</span>
              </Link>
            )}
            <Link
              to="/donate"
              className={`px-8 py-4 ${themeClasses.primary} text-white rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 group`}
              data-testid="donate-btn"
            >
              <Heart className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>{t(language, 'home.donate')}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Weekly Schedule Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-12 h-12 text-amber-600 dark:text-amber-500 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {language === 'en' ? 'Weekly Schedule' : 'ሰሙናዊ መደብ'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'en' ? 'Join us every Sunday' : 'ኩሉ ሰንበት ተሳተፉ'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Liturgy */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-600 rounded-full mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-2">
                {language === 'en' ? 'Divine Liturgy' : 'ቅዳሴ'}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 font-semibold mb-3">
                {language === 'en' ? 'Every Sunday at 7:00 AM' : 'ኩሉ ሰንበት ሰዓት 7:00 ንጉሆ'}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' ? 'Join us for our weekly Divine Liturgy service' : 'ንሰሙናዊ ቅዳሴ ምሳና ተሳተፉ'}
              </p>
            </div>

            {/* Sunday School */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-600 rounded-full mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-2">
                {language === 'en' ? 'Sunday School' : 'ሰንበት ቤት ትምህርቲ'}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 font-semibold mb-3">
                {language === 'en' ? 'Every Sunday at 10:30 AM' : 'ኩሉ ሰንበት ሰዓት 10:30 ንጉሆ'}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' ? 'Youth and children gathering with teachings' : 'መንእሰያትን ህፃናትን ምስ ትምህርትን'}
              </p>
            </div>

            {/* Women's Ministry */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-600 rounded-full mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-2">
                {language === 'en' ? "Women's Ministry" : 'ደቂ ኣንስትዮ አገልግሎት'}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 font-semibold mb-3">
                {language === 'en' ? 'Every Sunday at 11:30 AM' : 'ኩሉ ሰንበት ሰዓት 11:30 ንጉሆ'}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {language === 'en' ? 'Women gathering with fellowship and prayer' : 'ደቂ ኣንስትዮ ምስ ምስእትን ፀሎትን'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Announcements Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'home.announcements')}
          </h2>
          
          {Array.isArray(announcements) && announcements.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {announcements.map((announcement) => (
                <div 
                  key={announcement.id}
                  className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                    {language === 'en' ? announcement.title_en : announcement.title_ti}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {language === 'en' ? announcement.content_en : announcement.content_ti}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(announcement.date).toLocaleDateString(language === 'en' ? 'en-US' : 'ti-ER')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 dark:text-gray-400">
              {language === 'en' ? 'No announcements at this time' : 'እዚ ግዜ ምልክታት የለን'}
            </p>
          )}
        </div>
      </section>

      {/* Church Info Cards */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/about" className="group">
              <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-amber-700 dark:text-amber-500 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                  {t(language, 'about.historyTitle')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {language === 'en' ? 'Learn about our journey since 2016' : 'ካብ 2016 ጀሚሩ ኣገልግልቶ ይህብ ኣሎ'}
                </p>
                <div className="flex items-center text-amber-600 dark:text-amber-400 group-hover:translate-x-2 transition-transform">
                  <span className="font-semibold">{language === 'en' ? 'Learn More' : 'ኣብዚ ጠውቁ'}</span>
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </div>
            </Link>

            <Link to="/sermons" className="group">
              <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-amber-700 dark:text-amber-500 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                  {t(language, 'nav.sermons')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {language === 'en' ? 'Watch our weekly teachings and services' : 'ሰሙናዊ መድባትናን አገልግሎትናን ርኣዩ'}
                </p>
                <div className="flex items-center text-amber-600 dark:text-amber-400 group-hover:translate-x-2 transition-transform">
                  <span className="font-semibold">{language === 'en' ? 'Watch Now' : 'ኣብዚ ጠውቁ'}</span>
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </div>
            </Link>

            <Link to="/gallery" className="group">
              <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <h3 className="text-2xl font-bold text-amber-700 dark:text-amber-500 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
                  {t(language, 'nav.gallery')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {language === 'en' ? 'View moments from our community' : ' ብስእሊ ዝተሰነየ ንጥፈታትና ተመልከቱ'}
                </p>
                <div className="flex items-center text-amber-600 dark:text-amber-400 group-hover:translate-x-2 transition-transform">
                  <span className="font-semibold">{language === 'en' ? 'View Gallery' : 'ኣብዚ ጠውቁ'}</span>
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;